import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import { getArticleBySlug, articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || "");

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-200/70">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">该文章可能已被移除或链接有误</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryName =
    categories.find((c) => c.slug === article.category)?.name || article.category;

  const relatedArticles = articles
    .filter(
      (a) =>
        a.category === article.category && a.id !== article.id
    )
    .slice(0, 3);

  const getCategoryName = (catSlug: string) =>
    categories.find((c) => c.slug === catSlug)?.name || catSlug;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    author: { "@type": "Organization", name: article.author },
    datePublished: article.publishDate,
    image: article.imageUrl,
    url: `https://nervousnix.github.io/article/${article.slug}`,
    publisher: { "@type": "Organization", name: "第一次指南", url: "https://nervousnix.github.io" },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://nervousnix.github.io" },
      { "@type": "ListItem", position: 2, name: categoryName, item: `https://nervousnix.github.io/category/${article.category}` },
      { "@type": "ListItem", position: 3, name: article.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <Navbar />
      <main className="pt-16">
        <article>
          <div className="bg-background-100">
            <div className="max-w-4xl mx-auto px-6 md:px-10 py-8">
              <Breadcrumb
                items={[
                  { label: "首页", href: "/" },
                  { label: categoryName, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14">
            <header className="mb-10">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 whitespace-nowrap cursor-pointer"
                >
                  {categoryName}
                </Link>
                <span className="text-xs text-foreground-500 whitespace-nowrap">{article.readTime}</span>
                <span className="text-xs text-foreground-400 whitespace-nowrap">{article.publishDate}</span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-user-line text-primary-600 text-sm"></i>
                </div>
                <span className="text-sm text-foreground-600 font-medium">{article.author}</span>
              </div>
            </header>

            <div className="w-full aspect-[16/9] rounded-xl overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={`${article.title} - 第一次指南`}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{
                __html: article.content
                  .replace(/<h2/g, '<h2 class="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-12 mb-4"')
                  .replace(/<h3/g, '<h3 class="font-heading text-lg md:text-xl font-semibold text-foreground-900 mt-8 mb-3"')
                  .replace(/<p>/g, '<p class="text-foreground-700 leading-relaxed mb-4">')
                  .replace(/<ul>/g, '<ul class="list-disc pl-6 mb-4 space-y-2">')
                  .replace(/<li>/g, '<li class="text-foreground-700 leading-relaxed">')
                  .replace(/<strong>/g, '<strong class="font-semibold text-foreground-900">'),
              }}
            ></div>

            {article.tags && article.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t border-background-200/70">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs bg-background-200/70 text-foreground-600 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-18 bg-background-100">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard
                    key={ra.id}
                    slug={ra.slug}
                    title={ra.title}
                    category={ra.category}
                    excerpt={ra.excerpt}
                    author={ra.author}
                    publishDate={ra.publishDate}
                    readTime={ra.readTime}
                    imageUrl={ra.imageUrl}
                    categoryName={getCategoryName(ra.category)}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}